import React, { useEffect, useRef, useState } from 'react';
import { rowFor } from '../utils/rtl';
import { ActivityIndicator, Animated, KeyboardAvoidingView, NativeScrollEvent, NativeSyntheticEvent, Platform, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { AiBotIcon } from './icons/AppIcon';
import { WorkspaceProductResults } from './WorkspaceProductResults';
import { WorkspaceProposalCard } from './WorkspaceProposalCard';
import { WorkspaceAssistantText, WorkspaceTextLink } from './WorkspaceAssistantText';
import { WorkspaceDataTable } from './WorkspaceDataTable';
import { AttachmentTray, MessageAttachments, useWorkspaceAttachments } from './WorkspaceAttachments';
import { apiService } from '../services/api';
import { exportFileName, formatWorkspaceCell } from '../utils/workspaceExport';
import { savePdf } from '../utils/saveTextFile';
import { workspacePdfHtml } from '../utils/workspacePdf';
import { useAppStore } from '../store/appStore';
import { useWorkspaceAssistantStore } from '../store/workspaceAssistantStore';
import { useGlobalSearchStore } from '../store/globalSearchStore';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { usePermissions } from '../hooks/usePermissions';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { workspaceNavigation } from '../navigation/workspaceAssistantNavigation';
import type { WorkspaceAction, WorkspaceAttachment, WorkspaceChatMessage, WorkspaceTableRow } from '../services/workspace-assistant.types';
import type { TranslationKey } from '../i18n/en';
import { colors, spacing, typography } from '../theme';

const MAX_CHARS = 1200;

/** Starter cards: an area of the business and the questions it opens with (fill = put in the box to finish, e.g. a link). */
const STARTERS: Array<{ key: string; icon: keyof typeof Ionicons.glyphMap; prompts: Array<{ key: string; fill?: boolean }> }> = [
  { key: 'inbox', icon: 'chatbubbles-outline', prompts: [{ key: 'waiting' }, { key: 'today' }] },
  { key: 'customers', icon: 'people-outline', prompts: [{ key: 'find', fill: true }, { key: 'mine' }] },
  { key: 'products', icon: 'cube-outline', prompts: [{ key: 'coverage' }, { key: 'prices', fill: true }] },
  { key: 'messages', icon: 'megaphone-outline', prompts: [{ key: 'received' }, { key: 'template' }] },
  { key: 'analytics', icon: 'stats-chart-outline', prompts: [{ key: 'sales' }, { key: 'topCustomers' }] },
  { key: 'market', icon: 'pricetags-outline', prompts: [{ key: 'compare', fill: true }] },
];
/** Which starter area fits the screen the person is on. */
const AREA_BY_SCREEN: Record<string, string> = {
  chat: 'inbox', conversation: 'inbox', contacts: 'customers', home: 'analytics', 'contact-create': 'customers',
  'products-list': 'products', 'product-detail': 'products', vendors: 'products', catalogues: 'products', inventory: 'products',
  orders: 'analytics', quotations: 'analytics', dashboard: 'analytics', performance: 'analytics',
  'template-management': 'messages', 'send-whatsapp-message': 'messages', 'task-history': 'messages',
};
const SCREEN_LABELS = ['chat', 'conversation', 'contacts', 'home', 'products-list', 'product-detail', 'vendors', 'orders', 'quotations', 'dashboard', 'performance', 'template-management', 'lead-finder', 'flows-list', 'data-lists', 'settings'];

/** The step the server reports, in words. */
const stageKey = (stage?: string): TranslationKey => {
  const s = String(stage || '');
  const module = /^lookup_(.+)$/.exec(s)?.[1];
  const known = ['conversations', 'messages', 'contacts', 'products', 'product_coverage', 'web_prices', 'analytics', 'orders', 'quotations', 'vendors', 'templates', 'campaigns', 'automations', 'search_runs', 'search_results', 'data_lists', 'list_records', 'reports'];
  if (module) return (known.includes(module) ? `workspaceAssistant.stage.lookup.${module}` : 'workspaceAssistant.stage.lookup.other') as TranslationKey;
  if (s === 'history') return 'workspaceAssistant.stage.history';
  if (s === 'business' || s === 'catalogue') return `workspaceAssistant.stage.${s}` as TranslationKey;
  return 'workspaceAssistant.thinking';
};

const time = (iso: string, locale: string) => { try { return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

function TypingDots() {
  const styles = useThemedStyles(getStyles);
  const values = useRef([0, 1, 2].map(() => new Animated.Value(0.3))).current;
  useEffect(() => {
    const loop = Animated.loop(Animated.stagger(160, values.map(v => Animated.sequence([
      Animated.timing(v, { toValue: 1, duration: 320, useNativeDriver: Platform.OS !== 'web' }),
      Animated.timing(v, { toValue: 0.3, duration: 320, useNativeDriver: Platform.OS !== 'web' }),
    ]))));
    loop.start();
    return () => loop.stop();
  }, [values]);
  return <View style={styles.dots}>{values.map((v, i) => <Animated.View key={i} style={[styles.dot, { opacity: v }]} />)}</View>;
}

export function WorkspaceAssistant({ onClose, visible = true }: { onClose?: () => void; visible?: boolean }) {
  const styles = useThemedStyles(getStyles);
  const { t, isRTL, locale } = useLocale();
  const { navigateTo, currentScreen, startPhoneCall, isPhoneDialerReady, showNotification } = useAppContext();
  const businessId = useAppStore(s => s.currentBusiness?._id);
  const userId = useAppStore(s => s.user?.id);
  const role = useAppStore(s => s.businessRole);
  const { hasPermissionAtLeast } = usePermissions();
  const permitted = role === 'owner' || hasPermissionAtLeast('ai.useAssistant', 'edit');
  const scope = `${businessId}:${userId}`;
  const state = useWorkspaceAssistantStore();
  const active = state.scope === scope;
  const scroll = useRef<ScrollView>(null);
  const input = useRef<TextInput>(null);
  const atBottom = useRef(true);
  const [draft, setDraft] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [showJump, setShowJump] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  // The answer (or 'chat') whose PDF is being made.
  const [savingPdf, setSavingPdf] = useState<string | null>(null);
  const files = useWorkspaceAttachments();
  // The files of the question being answered, shown in its bubble until the reply arrives.
  const [sentFiles, setSentFiles] = useState<WorkspaceAttachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const root = useRef<any>(null);
  useEffect(() => { setDraft(''); setConfirmReset(false); files.clear(); }, [scope]);
  useEffect(() => {
    if (!visible || !businessId || !userId || !permitted) return;
    state.activate(scope);
    // One fetch on each visible screen/pane mount revalidates membership and data visibility.
    void state.load(scope);
  }, [scope, visible, permitted]);
  // A turn running elsewhere (another tab / after reload): follow it until it finishes.
  useEffect(() => {
    if (!visible || !permitted || !active || !state.serverPending || state.busy) return;
    const timer = setInterval(() => { void useWorkspaceAssistantStore.getState().load(scope); }, 3000);
    return () => clearInterval(timer);
  }, [scope, visible, permitted, active, state.serverPending?.requestId, state.busy]);
  // This tab's own question: show what the assistant is doing now.
  useEffect(() => {
    if (!visible || !active || !state.pending) return;
    const timer = setInterval(() => { void useWorkspaceAssistantStore.getState().pollStage(scope); }, 2500);
    return () => clearInterval(timer);
  }, [scope, visible, active, state.pending?.requestId]);

  const messages = active ? state.messages : [];
  const running = active && (state.pending || state.serverPending);
  const busy = active && (state.busy || state.loading || !!state.serverPending);
  const canType = permitted && active && state.ready && !busy;
  const send = (text = draft) => {
    const message = text.trim();
    const attached = files.ready;
    if ((!message && !attached.length) || files.uploading || busy || !active || !state.ready || message.length > MAX_CHARS) return;
    setDraft(''); setConfirmReset(false); files.clear(); setSentFiles(attached);
    atBottom.current = true;
    let timezone: string | undefined;
    try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* Assistant asks for timezone when needed. */ }
    void state.ask(scope, { message, requestId: `workspace_${Date.now()}_${Math.random().toString(36).slice(2)}`, currentScreen, locale, timezone,
      ...(attached.length ? { attachments: attached.map(a => a.id) } : {}) });
  };
  // Web: drop files on the panel, or paste a screenshot into it.
  useEffect(() => {
    const node: HTMLElement | null = Platform.OS === 'web' ? root.current : null;
    if (!node || !visible || !canType) return;
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const over = (e: DragEvent) => { if (hasFiles(e)) { e.preventDefault(); setDragging(true); } };
    const leave = (e: DragEvent) => { if (!node.contains(e.relatedTarget as Node)) setDragging(false); };
    const drop = (e: DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); setDragging(false); files.addFiles(e.dataTransfer?.files); };
    const paste = (e: ClipboardEvent) => { if (e.clipboardData?.files?.length && files.addFiles(e.clipboardData.files)) e.preventDefault(); };
    node.addEventListener('dragover', over); node.addEventListener('dragleave', leave); node.addEventListener('drop', drop); node.addEventListener('paste', paste);
    return () => { node.removeEventListener('dragover', over); node.removeEventListener('dragleave', leave); node.removeEventListener('drop', drop); node.removeEventListener('paste', paste); };
  }, [visible, canType, files.addFiles]);
  const fill = (text: string) => { setDraft(`${text} `); setTimeout(() => input.current?.focus(), 50); };
  // A question handed over from global search fills the box; the user reviews and sends it.
  const seed = useGlobalSearchStore((s) => s.assistantSeed);
  useEffect(() => {
    if (!seed || !visible || !permitted) return;
    useGlobalSearchStore.getState().setAssistantSeed('');
    setDraft(seed.slice(0, MAX_CHARS));
    setTimeout(() => input.current?.focus(), 80);
  }, [seed, visible, permitted]);
  const open = (action: WorkspaceAction) => {
    const route = workspaceNavigation(action);
    if (!route) return;
    onClose?.();
    navigateTo(route.screen, route.param, { ...route.routeParams, assistantNavigationKey: String(Date.now()) });
  };
  const failed = (message: string) => showNotification?.('error', t('workspaceAssistant.title'), message);
  // A customer row's chat: their WhatsApp conversation (created if they have none yet).
  const openChat = async (row: WorkspaceTableRow) => {
    if (row.chat) return open(row.chat);
    if (!row.contactId) return;
    try {
      const res: any = await apiService.authenticatedRequest('/api/omni-channel/conversations/findOrCreate', { method: 'POST', body: JSON.stringify({ contactId: row.contactId, channel: 'whatsapp' }) });
      const conversationId = res?.data?.conversationId;
      if (!conversationId) throw new Error();
      onClose?.();
      navigateTo('conversation', String(conversationId));
    } catch { failed(t('workspaceAssistant.table.chatFailed')); }
  };
  const call = async (row: WorkspaceTableRow) => {
    if (!row.contactId || !isPhoneDialerReady) return;
    try {
      const res = await apiService.getContactDialTarget(row.contactId);
      const phone = res.data?.phoneNumber;
      if (!phone) throw new Error();
      startPhoneCall(phone, row.contactId, { name: String(row.cells.name || phone) });
    } catch { failed(t('workspaceAssistant.table.callFailed')); }
  };
  const cellText = (value: any, column: any) => formatWorkspaceCell(value, column?.kind, locale);
  // PDF file: the answer with the question it answers (or the whole chat), charts and tables included.
  const pdf = (key: string, list: WorkspaceChatMessage[], title: string) => {
    if (savingPdf) return;
    setSavingPdf(key);
    savePdf(workspacePdfHtml({ title, messages: list, locale, format: cellText }), exportFileName(title, 'pdf'))
      .catch(() => failed(t('workspaceAssistant.pdfFailed'))).finally(() => setSavingPdf(null));
  };
  const answerPdf = (message: WorkspaceChatMessage) => {
    const question = [...messages.slice(0, messages.indexOf(message))].reverse().find(m => m.role === 'user');
    // Named after what was asked ("October sales plan"), else its first table.
    const title = question?.content.split('\n')[0].trim().slice(0, 80) || message.tables?.[0]?.title || t('workspaceAssistant.title');
    pdf(message.id, question ? [question, message] : [message], title);
  };
  // Names in the reply that are records it found: tappable.
  const textLinks = (message: WorkspaceChatMessage): WorkspaceTextLink[] => {
    const out = new Map<string, WorkspaceTextLink>();
    for (const table of message.tables || []) for (const r of table.rows) {
      const name = r.cells[table.columns[0]?.key];
      if (r.open && typeof name === 'string' && name.trim().length >= 3 && !out.has(name.trim())) out.set(name.trim(), { label: name.trim(), action: r.open });
    }
    for (const a of message.actions || []) if (a.param && a.label.trim().length >= 3 && !out.has(a.label.trim())) out.set(a.label.trim(), { label: a.label.trim(), action: a });
    return [...out.values()];
  };
  const copy = async (id: string, text: string) => {
    try { await Clipboard.setStringAsync(text); setCopied(id); setTimeout(() => setCopied(c => (c === id ? null : c)), 1500); } catch { /* clipboard unavailable */ }
  };
  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    atBottom.current = contentSize.height - (contentOffset.y + layoutMeasurement.height) < 80;
    if (atBottom.current && showJump) setShowJump(false);
  };
  const toBottom = () => { scroll.current?.scrollToEnd({ animated: true }); atBottom.current = true; setShowJump(false); };

  const row = { flexDirection: rowFor(isRTL) };
  const textAlign = { textAlign: isRTL ? 'right' as const : 'left' as const };
  const area = AREA_BY_SCREEN[currentScreen];
  const screenLabel = SCREEN_LABELS.includes(currentScreen) ? t(`workspaceAssistant.screen.${currentScreen}` as TranslationKey) : '';
  const starters = area ? [...STARTERS.filter(s => s.key === area), ...STARTERS.filter(s => s.key !== area)] : STARTERS;
  const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
  const questionBefore = (id: string) => { const i = messages.findIndex(m => m.id === id); return [...messages.slice(0, i)].reverse().find(m => m.role === 'user')?.content; };
  const stage = state.pending ? state.stage : state.serverPending?.stage;

  const canSend = canType && !files.uploading && (!!draft.trim() || !!files.ready.length);
  return <KeyboardAvoidingView style={styles.root} behavior={Platform.OS === 'ios' ? 'padding' : undefined}><View ref={root} style={styles.panel}>
    {dragging && <View pointerEvents="none" style={styles.dropZone}>
      <Ionicons name="cloud-upload-outline" size={28} color={colors.primary as string} />
      <Text style={styles.dropText}>{t('workspaceAssistant.attach.drop')}</Text>
    </View>}
    {/* Header */}
    <View style={[styles.header, row]}>
      <View style={styles.avatar}><AiBotIcon size={20} color={colors.primary as string} /></View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={[styles.title, textAlign]} numberOfLines={1}>{t('workspaceAssistant.title')}</Text>
        {screenLabel ? <View style={[styles.screenChip, isRTL ? { alignSelf: 'flex-end' } : { alignSelf: 'flex-start' }, row]}>
          <Ionicons name="locate-outline" size={11} color={colors.text.secondary as string} />
          <Text style={styles.screenChipText} numberOfLines={1}>{t('workspaceAssistant.onScreen', { screen: screenLabel })}</Text>
        </View> : <Text style={[styles.caption, textAlign]} numberOfLines={1}>{t('workspaceAssistant.subtitle')}</Text>}
      </View>
      <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.exportChatPdf')} {...(Platform.OS === 'web' ? { title: t('workspaceAssistant.exportChatPdf') } as any : {})}
        disabled={!messages.length || !!savingPdf} style={[styles.iconButton, !messages.length && { opacity: 0.35 }]} onPress={() => pdf('chat', messages, t('workspaceAssistant.title'))}>
        {savingPdf === 'chat' ? <ActivityIndicator size="small" color={colors.text.secondary as string} /> : <Ionicons name="download-outline" size={20} color={colors.text.secondary as string} />}
      </TouchableOpacity>
      <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.newChat')} {...(Platform.OS === 'web' ? { title: t('workspaceAssistant.newChat') } as any : {})}
        disabled={busy || !messages.length} style={[styles.iconButton, (busy || !messages.length) && { opacity: 0.35 }]} onPress={() => setConfirmReset(v => !v)}>
        <Ionicons name="add-circle-outline" size={22} color={colors.text.secondary as string} />
      </TouchableOpacity>
      {onClose && <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('common.close')} style={styles.iconButton} onPress={onClose}>
        <Ionicons name="close" size={22} color={colors.text.primary as string} />
      </TouchableOpacity>}
    </View>
    {confirmReset && <View style={[styles.banner, row]}>
      <Text style={[styles.caption, textAlign, { flex: 1 }]}>{t('workspaceAssistant.resetConfirm')}</Text>
      <TouchableOpacity accessibilityRole="button" style={styles.bannerGhost} onPress={() => setConfirmReset(false)}><Text style={styles.caption}>{t('common.cancel')}</Text></TouchableOpacity>
      <TouchableOpacity accessibilityRole="button" disabled={busy} style={styles.bannerButton} onPress={() => { setConfirmReset(false); void state.clear(scope); }}>
        <Text style={styles.bannerButtonText}>{t('workspaceAssistant.newChat')}</Text>
      </TouchableOpacity>
    </View>}

    {/* Conversation */}
    <View style={{ flex: 1, minHeight: 0 }}>
      <ScrollView ref={scroll} style={{ flex: 1 }} contentContainerStyle={styles.feed} keyboardShouldPersistTaps="handled" onScroll={onScroll} scrollEventThrottle={64}
        onContentSizeChange={() => { if (atBottom.current) scroll.current?.scrollToEnd({ animated: true }); else setShowJump(true); }}>
        {!permitted ? <View style={styles.empty}>
          <Ionicons name="lock-closed-outline" size={28} color={colors.text.secondary as string} />
          <Text style={[styles.caption, { textAlign: 'center' }]}>{t('workspaceAssistant.permission')}</Text>
        </View> : <>
          {!messages.length && !running && <View style={styles.welcome}>
            <View style={styles.welcomeIcon}><AiBotIcon size={30} color={colors.primary as string} /></View>
            <Text style={[styles.welcomeTitle, { textAlign: 'center' }]}>{t('workspaceAssistant.welcome')}</Text>
            <Text style={[styles.caption, { textAlign: 'center' }]}>{t('workspaceAssistant.intro')}</Text>
            <View style={[styles.cards, row]}>
              {starters.map(starter => <View key={starter.key} style={[styles.card, starter.key === area && styles.cardHere]}>
                <View style={[row, { alignItems: 'center', gap: 6 }]}>
                  <Ionicons name={starter.icon} size={16} color={colors.primary as string} />
                  <Text style={[styles.cardTitle, textAlign]} numberOfLines={1}>{t(`workspaceAssistant.area.${starter.key}` as TranslationKey)}</Text>
                </View>
                {starter.prompts.map(p => {
                  const text = t(`workspaceAssistant.starter.${starter.key}.${p.key}` as TranslationKey);
                  return <TouchableOpacity key={p.key} accessibilityRole="button" disabled={!active || !state.ready} style={[styles.prompt, row]} onPress={() => (p.fill ? fill(text) : send(text))}>
                    <Text style={[styles.promptText, textAlign, { flex: 1 }]}>{text}</Text>
                    <Ionicons name={p.fill ? 'create-outline' : isRTL ? 'arrow-back' : 'arrow-forward'} size={13} color={colors.text.secondary as string} />
                  </TouchableOpacity>;
                })}
              </View>)}
            </View>
          </View>}

          {messages.map((message, index) => {
            const mine = message.role === 'user';
            const isLast = message === lastAssistant;
            const links = (message.actions || []).filter(action => !message.productResults?.items.some(p => p.actionId === action.id));
            if (mine) return <View key={message.id} style={[styles.userRow, row]}>
              <View style={styles.userColumn}>
                {!!message.attachments?.length && <MessageAttachments attachments={message.attachments} isRTL={isRTL} />}
                <View style={styles.userBubble}><Text selectable style={[styles.body, textAlign]}>{message.content}</Text></View>
              </View>
            </View>;
            const firstOfTurn = index === 0 || messages[index - 1].role !== 'assistant';
            return <View key={message.id} style={[styles.assistantRow, row]}>
              <View style={[styles.miniAvatar, !firstOfTurn && { opacity: 0 }]}><AiBotIcon size={14} color={colors.primary as string} /></View>
              <View style={styles.assistantBody}>
                {message.productResults?.items.length ? <WorkspaceProductResults results={message.productResults} actions={message.actions || []} onOpen={open} />
                  : <WorkspaceAssistantText content={message.content} isRTL={isRTL} links={textLinks(message)} onOpen={open} />}
                {message.tables?.map(table => <WorkspaceDataTable key={table.id} table={table} busy={!!busy} canCall={isPhoneDialerReady}
                  loadingMore={state.loadingRows === `${message.id}:${table.id}`} onOpen={open} onChat={row => void openChat(row)} onCall={row => void call(row)}
                  onMore={() => void state.moreRows(scope, message.id, table.id)} />)}
                {message.proposal && <WorkspaceProposalCard proposal={message.proposal} businessId={businessId} busy={busy}
                  onExecute={(id, body) => void state.execute(scope, id, body)} onOpen={open} />}
                {message.document && <TouchableOpacity accessibilityRole="button" disabled={!!savingPdf} style={[styles.pdfButton, row, isRTL ? { alignSelf: 'flex-end' } : { alignSelf: 'flex-start' }]} onPress={() => answerPdf(message)}>
                  {savingPdf === message.id ? <ActivityIndicator size="small" color="#ffffff" /> : <Ionicons name="document-text-outline" size={16} color="#ffffff" />}
                  <Text style={styles.pdfButtonText}>{t(savingPdf === message.id ? 'workspaceAssistant.pdfSaving' : 'workspaceAssistant.downloadPdf')}</Text>
                </TouchableOpacity>}
                {!!links.length && <View style={[styles.chips, row]}>{links.map(action => <TouchableOpacity key={action.id} accessibilityRole="button" style={[styles.linkChip, row]} onPress={() => open(action)}>
                  <Ionicons name={isRTL ? 'arrow-back-circle-outline' : 'arrow-forward-circle-outline'} size={15} color={colors.primary as string} />
                  <Text style={styles.linkChipText} numberOfLines={1}>{action.label}</Text>
                </TouchableOpacity>)}</View>}
                <View style={[styles.meta, row]}>
                  <Text style={styles.metaText}>{time(message.createdAt, locale)}</Text>
                  <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.copy')} style={styles.metaButton} onPress={() => copy(message.id, message.content)}>
                    <Ionicons name={copied === message.id ? 'checkmark' : 'copy-outline'} size={14} color={colors.text.secondary as string} />
                  </TouchableOpacity>
                  {!message.document && <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.savePdf')} {...(Platform.OS === 'web' ? { title: t('workspaceAssistant.savePdf') } as any : {})}
                    disabled={!!savingPdf} style={styles.metaButton} onPress={() => answerPdf(message)}>
                    {savingPdf === message.id ? <ActivityIndicator size="small" color={colors.text.secondary as string} /> : <Ionicons name="download-outline" size={14} color={colors.text.secondary as string} />}
                  </TouchableOpacity>}
                  {isLast && !busy && questionBefore(message.id) && <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.askAgain')} style={styles.metaButton}
                    onPress={() => send(questionBefore(message.id)!)}>
                    <Ionicons name="refresh-outline" size={14} color={colors.text.secondary as string} />
                  </TouchableOpacity>}
                </View>
                {isLast && !!message.followUps?.length && <View style={[styles.chips, row]}>{message.followUps.map((question, i) => <TouchableOpacity accessibilityRole="button" key={i} disabled={busy} style={styles.followChip} onPress={() => send(question)}>
                  <Text style={[styles.followChipText, textAlign]}>{question}</Text>
                </TouchableOpacity>)}</View>}
              </View>
            </View>;
          })}

          {running && ((state.pending || state.serverPending)?.message || (state.pending && sentFiles.length)) ? <View style={[styles.userRow, row]}>
            <View style={styles.userColumn}>
              {!!state.pending && !!sentFiles.length && <MessageAttachments attachments={sentFiles} isRTL={isRTL} />}
              {!!(state.pending || state.serverPending)?.message && <View style={styles.userBubble}><Text style={[styles.body, textAlign]}>{(state.pending || state.serverPending)?.message}</Text></View>}
            </View>
          </View> : null}
          {running && <View style={[styles.assistantRow, row]}>
            <View style={styles.miniAvatar}><AiBotIcon size={14} color={colors.primary as string} /></View>
            <View style={[styles.typing, row]}><TypingDots /><Text style={styles.caption}>{t(stageKey(stage))}</Text></View>
          </View>}
          {!running && busy && state.loading && !messages.length && <ActivityIndicator style={{ marginTop: spacing.lg }} color={colors.primary as string} />}

          {active && !!state.error && <View style={[styles.errorBox, row]}>
            <Ionicons name="alert-circle" size={18} color={colors.status.error as string} />
            <View style={{ flex: 1, gap: spacing.xs }}>
              <Text accessibilityRole="alert" style={[styles.errorText, textAlign]}>{state.error}</Text>
              <View style={[row, { gap: spacing.sm, flexWrap: 'wrap' }]}>
                <TouchableOpacity accessibilityRole="button" disabled={busy} style={styles.bannerButton} onPress={() => state.failed ? void state.ask(scope, state.failed) : void state.load(scope)}>
                  <Text style={styles.bannerButtonText}>{t('common.retry')}</Text>
                </TouchableOpacity>
                {!state.ready && hasPermissionAtLeast('ai.manageConfig', 'view') && <TouchableOpacity accessibilityRole="button" style={styles.bannerGhost} onPress={() => { onClose?.(); navigateTo('ai-setup'); }}>
                  <Text style={styles.caption}>{t('workspaceAssistant.setup')}</Text>
                </TouchableOpacity>}
              </View>
            </View>
          </View>}
        </>}
      </ScrollView>
      {showJump && <TouchableOpacity accessibilityRole="button" style={[styles.jump, row]} onPress={toBottom}>
        <Ionicons name="arrow-down" size={14} color={colors.text.onPrimary as string} />
        <Text style={styles.jumpText}>{t('workspaceAssistant.newReply')}</Text>
      </TouchableOpacity>}
    </View>

    {/* Composer */}
    <View style={styles.composerArea}>
      <AttachmentTray items={files.items} onRemove={files.remove} isRTL={isRTL} />
      <View style={[styles.composer, row, !canType && styles.composerOff]}>
        <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.attach.add')} {...(Platform.OS === 'web' ? { title: t('workspaceAssistant.attach.add') } as any : {})}
          disabled={!canType || files.full} style={[styles.attach, (!canType || files.full) && { opacity: 0.35 }]} onPress={() => void files.pick()}>
          <Ionicons name="attach" size={22} color={colors.text.secondary as string} />
        </TouchableOpacity>
        <TextInput ref={input} value={draft} onChangeText={setDraft} multiline maxLength={MAX_CHARS} editable={canType}
          accessibilityLabel={t('workspaceAssistant.placeholder')} placeholder={t('workspaceAssistant.placeholder')} placeholderTextColor={colors.text.secondary as string}
          style={[styles.input, textAlign, Platform.OS === 'web' && ({ outlineStyle: 'none' } as any)]} onKeyPress={event => {
            if (Platform.OS === 'web' && event.nativeEvent.key === 'Enter' && !(event as any).shiftKey && !(event.nativeEvent as any).shiftKey) { event.preventDefault(); send(); }
          }} />
        {running ? <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.stop')} style={styles.send} onPress={() => void state.stop(scope)}>
          <Ionicons name="stop" size={16} color={colors.text.onPrimary as string} />
        </TouchableOpacity> : <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.send')} disabled={!canSend}
          style={[styles.send, !canSend && { opacity: 0.35 }]} onPress={() => send()}>
          <Ionicons name="arrow-up" size={20} color={colors.text.onPrimary as string} />
        </TouchableOpacity>}
      </View>
      <View style={[styles.composerMeta, row]}>
        {Platform.OS === 'web' ? <Text style={styles.metaText}>{t('workspaceAssistant.enterHint')}</Text> : <View />}
        {draft.length > MAX_CHARS - 200 ? <Text style={[styles.metaText, draft.length >= MAX_CHARS && { color: colors.status.error as string }]}>{draft.length}/{MAX_CHARS}</Text> : null}
      </View>
    </View>
  </View></KeyboardAvoidingView>;
}

const getStyles = () => StyleSheet.create({
  root: { flex: 1, minHeight: 0, backgroundColor: colors.background.surface },
  panel: { flex: 1, minHeight: 0 },
  header: { alignItems: 'center', paddingHorizontal: spacing.md, paddingVertical: 10, gap: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  avatar: { width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryTransparent },
  title: { fontFamily: typography.fontFamily.semibold, fontSize: 15, color: colors.text.primary },
  caption: { fontSize: 12, lineHeight: 18, color: colors.text.secondary },
  screenChip: { alignItems: 'center', gap: 4, marginTop: 2, paddingHorizontal: 7, paddingVertical: 2, borderRadius: 999, backgroundColor: colors.background.secondary, maxWidth: '100%' },
  screenChipText: { fontSize: 11, color: colors.text.secondary },
  iconButton: { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center' },
  banner: { alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, backgroundColor: colors.background.secondary, borderBottomWidth: 1, borderBottomColor: colors.border },
  bannerButton: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8, backgroundColor: colors.primary },
  bannerButtonText: { color: colors.text.onPrimary, fontSize: 12, fontFamily: typography.fontFamily.semibold },
  bannerGhost: { paddingHorizontal: 10, paddingVertical: 7, borderRadius: 8 },
  feed: { padding: spacing.md, gap: 14, flexGrow: 1 },
  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing.sm, padding: spacing.lg },
  welcome: { alignItems: 'center', gap: spacing.sm, paddingTop: spacing.md },
  welcomeIcon: { width: 56, height: 56, borderRadius: 28, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryTransparent, marginBottom: 4 },
  welcomeTitle: { fontFamily: typography.fontFamily.semibold, fontSize: 17, color: colors.text.primary },
  cards: { flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm, width: '100%' },
  card: { flexGrow: 1, flexBasis: 220, gap: 6, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  cardHere: { borderColor: colors.primary },
  cardTitle: { fontSize: 13, fontFamily: typography.fontFamily.semibold, color: colors.text.primary, flexShrink: 1 },
  prompt: { alignItems: 'center', gap: 6, paddingVertical: 7, paddingHorizontal: 8, borderRadius: 8, backgroundColor: colors.background.secondary },
  promptText: { fontSize: 12.5, lineHeight: 18, color: colors.text.primary },
  userRow: { justifyContent: 'flex-end' },
  userColumn: { maxWidth: '85%', alignItems: 'flex-end' },
  userBubble: { maxWidth: '100%', paddingHorizontal: 14, paddingVertical: 9, borderRadius: 16, backgroundColor: colors.primaryTransparent },
  body: { color: colors.text.primary, fontSize: 14, lineHeight: 22 },
  assistantRow: { alignItems: 'flex-start', gap: 10 },
  miniAvatar: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryTransparent, marginTop: 2 },
  assistantBody: { flex: 1, minWidth: 0, gap: spacing.sm },
  chips: { flexWrap: 'wrap', gap: 6 },
  pdfButton: { alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999, backgroundColor: colors.primary, marginTop: 6 },
  pdfButtonText: { color: '#ffffff', fontSize: 13, fontFamily: typography.fontFamily.semibold },
  linkChip: { alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999, borderWidth: 1, borderColor: colors.border, maxWidth: '100%' },
  linkChipText: { color: colors.primary, fontSize: 12.5, fontFamily: typography.fontFamily.semibold, flexShrink: 1 },
  followChip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999, backgroundColor: colors.background.secondary, maxWidth: '100%' },
  followChipText: { color: colors.text.primary, fontSize: 12.5, lineHeight: 18 },
  meta: { alignItems: 'center', gap: 4 },
  metaText: { fontSize: 11, color: colors.text.secondary },
  metaButton: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center' },
  typing: { alignItems: 'center', gap: spacing.sm, paddingVertical: 6 },
  dots: { flexDirection: 'row', gap: 4, alignItems: 'center' },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.primary },
  errorBox: { gap: spacing.sm, padding: 12, borderRadius: 12, borderWidth: 1, borderColor: colors.status.error, backgroundColor: String(colors.status.error) + '12', alignItems: 'flex-start' },
  errorText: { fontSize: 13, lineHeight: 19, color: colors.text.primary },
  jump: { position: 'absolute', bottom: 10, alignSelf: 'center', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, backgroundColor: colors.primary },
  jumpText: { color: colors.text.onPrimary, fontSize: 12, fontFamily: typography.fontFamily.semibold },
  composerArea: { paddingHorizontal: spacing.md, paddingTop: spacing.sm, paddingBottom: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border, gap: 4 },
  attach: { width: 34, height: 36, alignItems: 'center', justifyContent: 'center' },
  dropZone: { position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, zIndex: 20, alignItems: 'center', justifyContent: 'center', gap: 6, margin: 8, borderRadius: 14, borderWidth: 2, borderStyle: 'dashed', borderColor: colors.primary, backgroundColor: colors.background.surface },
  dropText: { fontSize: 14, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  composer: { alignItems: 'flex-end', gap: spacing.sm, paddingStart: 4, paddingEnd: 5, paddingVertical: 5, borderRadius: 22, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.primary },
  composerOff: { opacity: 0.7 },
  input: { flex: 1, minHeight: 36, maxHeight: 150, paddingVertical: 8, color: colors.text.primary, fontSize: 14 },
  send: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primary },
  composerMeta: { justifyContent: 'space-between', paddingHorizontal: 6, minHeight: 14 },
});
